import { ComponentType, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@artstorefronts/ui';
import { Search, X, ArrowLeftRight, Pin } from 'lucide-react';
import { FixedSizeList as _FixedSizeList_, FixedSizeListProps, ListChildComponentProps } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';

import { GET_COMPARE } from '../../graphql/admin-artist-genome-queries';

const List = _FixedSizeList_ as unknown as ComponentType<FixedSizeListProps>;
const ARTIST_ROW_HEIGHT = 44;

interface GenomePoint {
  userId: string;
  displayName: string | null;
  avatarUrl: string | null;
  x: number;
  y: number;
  sampleCount: number;
}

interface GenomeNeighbor {
  userId: string;
  displayName: string | null;
  avatarUrl: string | null;
  similarity: number;
  concordance: number | null;
  latitude: number | null;
  longitude: number | null;
  city: string | null;
  region: string | null;
  country: string | null;
  countryCode: string | null;
  sampleCount: number;
}

interface CompareProfile {
  userId: string;
  displayName: string | null;
  avatarUrl: string | null;
  bio: string | null;
  artistStatement: string | null;
  city: string | null;
  region: string | null;
  country: string | null;
  imageCount: number;
  sampleImages: string[];
}

interface CompareData {
  artistA: CompareProfile;
  artistB: CompareProfile;
  visualSimilarity: number | null;
  identitySimilarity: number | null;
}

interface GenomeDetailPanelProps {
  points: GenomePoint[];
  neighbors: GenomeNeighbor[];
  anchorUserId: string | null;
  pinnedUserIds: string[];
  axis: string;
  viewMode: 'graph' | 'map';
  onSelectArtist: (userId: string) => void;
  onCompare: (userId: string) => void;
  compareData: CompareData | null;
  compareLoading: boolean;
  onCloseCompare: () => void;
  onUnpinArtist: (userId: string) => void;
  onClearAnchor: () => void;
}

function ConcordanceBadges({ concordance }: { concordance: number | null }) {
  if (concordance == null) return null;
  const colors: Record<number, string> = {
    1: 'bg-blue-100 text-blue-700',
    2: 'bg-orange-100 text-orange-700',
    3: 'bg-red-100 text-red-700',
  };
  return (
    <span
      className={`inline-flex h-4 min-w-[1.75rem] items-center justify-center rounded px-1 text-[9px] font-semibold ${
        colors[concordance] ?? 'bg-zinc-100 text-zinc-400'
      }`}
    >
      {concordance}/3
    </span>
  );
}

function Avatar({ url, name, size = 'sm' }: { url: string | null; name: string | null; size?: 'sm' | 'md' }) {
  const dims = size === 'md' ? 'h-9 w-9 text-xs' : 'h-6 w-6 text-[10px]';
  if (url) {
    return <img src={url} alt="" className={`${dims} shrink-0 rounded-full object-cover`} />;
  }
  return (
    <div className={`${dims} flex shrink-0 items-center justify-center rounded-full bg-zinc-200 text-zinc-500`}>
      {(name ?? '?')[0]}
    </div>
  );
}

function NeighborRow({
  neighbor,
  showConcordance,
  onClick,
  onCompare,
}: {
  neighbor: GenomeNeighbor;
  showConcordance: boolean;
  onClick: () => void;
  onCompare: () => void;
}) {
  return (
    <div className="group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-zinc-100">
      <button onClick={onClick} className="flex min-w-0 flex-1 items-center gap-2 text-left">
        <Avatar url={neighbor.avatarUrl} name={neighbor.displayName} />
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-zinc-900">{neighbor.displayName ?? 'Unknown'}</p>
          <p className="truncate text-zinc-500">
            {neighbor.similarity.toFixed(4)}
            {neighbor.city ? ` · ${neighbor.city}` : ''}
          </p>
        </div>
      </button>
      {showConcordance && <ConcordanceBadges concordance={neighbor.concordance} />}
      <button
        onClick={onCompare}
        className="shrink-0 rounded p-1 text-zinc-400 opacity-0 transition-opacity hover:bg-zinc-200 hover:text-zinc-600 group-hover:opacity-100"
        title="Compare"
      >
        <ArrowLeftRight className="h-3 w-3" />
      </button>
    </div>
  );
}

function ArtistRow({ point, selected, onClick }: { point: GenomePoint; selected: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-xs transition-colors hover:bg-zinc-100 ${
        selected ? 'bg-zinc-100' : ''
      }`}
    >
      <Avatar url={point.avatarUrl} name={point.displayName} />
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-zinc-900">{point.displayName ?? 'Unknown'}</p>
        <p className="truncate text-zinc-500">{point.sampleCount} samples</p>
      </div>
    </button>
  );
}

interface ArtistRowVirtualData {
  points: GenomePoint[];
  anchorUserId: string | null;
  onSelectArtist: (userId: string) => void;
}

function ArtistRowVirtual({ index, style, data }: ListChildComponentProps<ArtistRowVirtualData>) {
  const point = data.points[index];
  if (!point) return null;
  return (
    <div style={style}>
      <ArtistRow
        point={point}
        selected={point.userId === data.anchorUserId}
        onClick={() => data.onSelectArtist(point.userId)}
      />
    </div>
  );
}

function SimilarityBar({ label, value }: { label: string; value: number | null }) {
  if (value == null) return null;
  const pct = Math.round(value * 100);
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-14 shrink-0 text-[10px] text-zinc-500">{label}</span>
      <div className="h-1 flex-1 rounded-full bg-zinc-100">
        <div className="h-full rounded-full bg-blue-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-9 shrink-0 text-right text-[10px] font-medium text-zinc-700">{value.toFixed(2)}</span>
    </div>
  );
}

function ProfileCard({ profile }: { profile: CompareProfile }) {
  const location = [profile.city, profile.region, profile.country].filter(Boolean).join(', ');
  return (
    <div className="flex-1 space-y-2">
      <div className="flex items-center gap-2">
        <Avatar url={profile.avatarUrl} name={profile.displayName} />
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-zinc-900">{profile.displayName ?? 'Unknown'}</p>
          {location && <p className="truncate text-[10px] text-zinc-500">{location}</p>}
        </div>
      </div>

      {profile.sampleImages.length > 0 && (
        <div className="grid grid-cols-4 gap-1">
          {profile.sampleImages.slice(0, 8).map((url, i) => (
            <img key={i} src={url} alt="" className="h-16 w-16 rounded object-cover" />
          ))}
        </div>
      )}

      {profile.bio && <p className="line-clamp-3 text-[11px] text-zinc-600">{profile.bio}</p>}
      {profile.artistStatement && (
        <p className="line-clamp-2 text-[11px] italic text-zinc-500">{profile.artistStatement}</p>
      )}
      <p className="text-[10px] text-zinc-400">{profile.imageCount} images</p>
    </div>
  );
}

function CompareView({ data, onClose }: { data: CompareData; onClose: () => void }) {
  return (
    <div className="flex flex-1 flex-col overflow-y-auto">
      <div className="flex items-center justify-between border-b border-zinc-200 px-3 py-2">
        <p className="text-xs font-medium text-zinc-900">
          {data.artistA.displayName ?? 'Artist A'} ↔ {data.artistB.displayName ?? 'Artist B'}
        </p>
        <button onClick={onClose} className="rounded p-1 hover:bg-zinc-100">
          <X className="h-3.5 w-3.5 text-zinc-400" />
        </button>
      </div>

      <div className="space-y-4 p-3">
        <div className="space-y-1.5">
          <SimilarityBar label="Visual" value={data.visualSimilarity} />
          <SimilarityBar label="Identity" value={data.identitySimilarity} />
        </div>

        <div className="flex gap-3">
          <ProfileCard profile={data.artistA} />
          <ProfileCard profile={data.artistB} />
        </div>
      </div>
    </div>
  );
}

function PreviewCard({
  profile,
  similarities,
  isAnchor,
  onRemove,
}: {
  profile: CompareProfile;
  similarities: { visual: number | null; identity: number | null } | null;
  isAnchor: boolean;
  onRemove: () => void;
}) {
  const location = [profile.city, profile.region, profile.country].filter(Boolean).join(', ');
  return (
    <div className={`rounded-md border bg-white p-2.5 ${isAnchor ? 'border-green-300 shadow-sm' : 'border-zinc-200'}`}>
      <div className="flex items-start gap-2">
        <Avatar url={profile.avatarUrl} name={profile.displayName} size="md" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {isAnchor && <Pin className="h-3 w-3 shrink-0 text-green-600" />}
            <p className="truncate text-xs font-medium text-zinc-900">{profile.displayName ?? 'Unknown'}</p>
          </div>
          {location && <p className="truncate text-[10px] text-zinc-500">{location}</p>}
          <p className="text-[10px] text-zinc-400">{profile.imageCount} images</p>
        </div>
        <button
          onClick={onRemove}
          className="shrink-0 rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
          title={isAnchor ? 'Clear selection' : 'Unpin'}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {profile.sampleImages.length > 0 && (
        <div className="mt-2 grid grid-cols-4 gap-1">
          {profile.sampleImages.slice(0, 4).map((url, i) => (
            <img key={i} src={url} alt="" className="aspect-square w-full rounded object-cover" />
          ))}
        </div>
      )}

      {similarities && (
        <div className="mt-2 space-y-1">
          <SimilarityBar label="Visual" value={similarities.visual} />
          <SimilarityBar label="Identity" value={similarities.identity} />
        </div>
      )}
    </div>
  );
}

function PreviewCardFetcher({
  anchorUserId,
  targetUserId,
  isAnchor,
  onRemove,
}: {
  anchorUserId: string;
  targetUserId: string;
  isAnchor: boolean;
  onRemove: () => void;
}) {
  const { data, loading } = useQuery(GET_COMPARE, {
    variables: { userIdA: anchorUserId, userIdB: targetUserId },
    fetchPolicy: 'cache-first',
  });

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-zinc-200 bg-white p-2.5 text-xs text-zinc-400">
        <div className="h-9 w-9 animate-pulse rounded-full bg-zinc-100" />
        <span>Loading…</span>
      </div>
    );
  }

  const result = data?.adminArtistGenomeCompare;
  if (!result) return null;

  const profile: CompareProfile = isAnchor ? result.artistA : result.artistB;
  const similarities = isAnchor
    ? null
    : {
        visual: result.visualSimilarity,
        identity: result.identitySimilarity,
      };

  return <PreviewCard profile={profile} similarities={similarities} isAnchor={isAnchor} onRemove={onRemove} />;
}

function GenomeDetailPanel({
  points,
  neighbors,
  anchorUserId,
  pinnedUserIds,
  axis,
  viewMode,
  onSelectArtist,
  onCompare,
  compareData,
  compareLoading,
  onCloseCompare,
  onUnpinArtist,
  onClearAnchor,
}: GenomeDetailPanelProps) {
  const [search, setSearch] = useState('');

  const showConcordance = axis === 'COMBINED';
  const hasPins = pinnedUserIds.length > 0;

  const stats = useMemo(() => {
    const totalConnections = neighbors.reduce((s, n) => s + n.similarity, 0);
    const concordance3 = neighbors.filter((n) => n.concordance != null && n.concordance >= 3).length;
    const concordance2 = neighbors.filter(
      (n) => n.concordance != null && n.concordance >= 2 && n.concordance < 3
    ).length;
    return {
      totalArtists: points.length,
      avgConnections: neighbors.length ? (totalConnections / neighbors.length).toFixed(2) : '0',
      concordance3,
      concordance2,
    };
  }, [points, neighbors]);

  const filteredPoints = useMemo(() => {
    if (!search.trim()) return points;
    const q = search.toLowerCase();
    return points.filter((p) => p.displayName?.toLowerCase().includes(q));
  }, [points, search]);

  const { withLocation, noLocation } = useMemo(() => {
    const withLoc = neighbors.filter((n) => n.latitude != null && n.longitude != null);
    const noLoc = neighbors.filter((n) => n.latitude == null || n.longitude == null);
    return { withLocation: withLoc, noLocation: noLoc };
  }, [neighbors]);

  if (compareData) {
    return (
      <div className="flex h-full w-96 flex-col border-l border-zinc-200 bg-white">
        <CompareView data={compareData} onClose={onCloseCompare} />
      </div>
    );
  }

  if (compareLoading) {
    return (
      <div className="flex h-full w-96 flex-col items-center justify-center border-l border-zinc-200 bg-white">
        <p className="text-xs text-zinc-400">Loading comparison...</p>
      </div>
    );
  }

  return (
    <div className="flex h-full w-96 flex-col border-l border-zinc-200 bg-white">
      {anchorUserId && (
        <div
          className={`space-y-2 border-b border-zinc-200 bg-zinc-50 p-3 ${
            hasPins ? 'flex-1 overflow-y-auto' : 'max-h-[50%] shrink-0 overflow-y-auto'
          }`}
        >
          <PreviewCardFetcher
            anchorUserId={anchorUserId}
            targetUserId={anchorUserId}
            isAnchor
            onRemove={onClearAnchor}
          />
          {pinnedUserIds.map((pinId) => (
            <PreviewCardFetcher
              key={pinId}
              anchorUserId={anchorUserId}
              targetUserId={pinId}
              isAnchor={false}
              onRemove={() => onUnpinArtist(pinId)}
            />
          ))}
        </div>
      )}

      {!hasPins && (
        <>
          <div className="grid grid-cols-4 gap-px border-b border-zinc-200 bg-zinc-100">
            {[
              { label: 'Artists', value: stats.totalArtists },
              { label: 'Avg Sim', value: stats.avgConnections },
              ...(showConcordance
                ? [
                    { label: '3-Axis', value: stats.concordance3 },
                    { label: '2-Axis', value: stats.concordance2 },
                  ]
                : [
                    { label: 'Neighbors', value: neighbors.length },
                    { label: 'Selected', value: anchorUserId ? '1' : '—' },
                  ]),
            ].map((s) => (
              <div key={s.label} className="bg-white px-3 py-2 text-center">
                <p className="text-sm font-semibold text-zinc-900">{s.value}</p>
                <p className="text-[10px] text-zinc-500">{s.label}</p>
              </div>
            ))}
          </div>

          <Tabs defaultValue="neighbors" className="flex flex-1 flex-col overflow-hidden">
            <TabsList className="mx-3 mt-3 shrink-0">
              <TabsTrigger value="neighbors" className="text-xs">
                Neighbors
              </TabsTrigger>
              <TabsTrigger value="all" className="text-xs">
                All Artists
              </TabsTrigger>
            </TabsList>

            <TabsContent value="neighbors" className="flex-1 overflow-y-auto px-3 pb-3">
              {!anchorUserId ? (
                <p className="py-4 text-center text-xs text-zinc-400">Select an artist to see neighbors</p>
              ) : neighbors.length === 0 ? (
                <p className="py-4 text-center text-xs text-zinc-400">No neighbors found</p>
              ) : viewMode === 'map' ? (
                <div className="space-y-3">
                  {withLocation.length > 0 && (
                    <div>
                      <p className="mb-1 text-[10px] font-medium uppercase tracking-wider text-zinc-400">
                        With location ({withLocation.length})
                      </p>
                      <div className="space-y-0.5">
                        {withLocation.map((n) => (
                          <NeighborRow
                            key={n.userId}
                            neighbor={n}
                            showConcordance={showConcordance}
                            onClick={() => onSelectArtist(n.userId)}
                            onCompare={() => onCompare(n.userId)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {noLocation.length > 0 && (
                    <div>
                      <p className="mb-1 text-[10px] font-medium uppercase tracking-wider text-zinc-400">
                        No location ({noLocation.length})
                      </p>
                      <div className="space-y-0.5">
                        {noLocation.map((n) => (
                          <NeighborRow
                            key={n.userId}
                            neighbor={n}
                            showConcordance={showConcordance}
                            onClick={() => onSelectArtist(n.userId)}
                            onCompare={() => onCompare(n.userId)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-0.5">
                  {neighbors.map((n) => (
                    <NeighborRow
                      key={n.userId}
                      neighbor={n}
                      showConcordance={showConcordance}
                      onClick={() => onSelectArtist(n.userId)}
                      onCompare={() => onCompare(n.userId)}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="all" className="flex flex-1 flex-col overflow-hidden px-3 pb-3">
              <div className="relative mb-2">
                <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search artists..."
                  className="w-full rounded-md border border-zinc-200 py-1.5 pl-7 pr-2 text-xs outline-none focus:border-zinc-400"
                />
              </div>
              <div className="flex-1 overflow-hidden">
                {filteredPoints.length === 0 ? (
                  <p className="py-4 text-center text-xs text-zinc-400">No artists found</p>
                ) : (
                  <AutoSizer>
                    {({ height, width }: { height: number; width: number }) => (
                      <List
                        height={height}
                        width={width}
                        itemCount={filteredPoints.length}
                        itemSize={ARTIST_ROW_HEIGHT}
                        itemKey={(index: number) => filteredPoints[index]!.userId}
                        itemData={{
                          points: filteredPoints,
                          anchorUserId,
                          onSelectArtist,
                        }}
                      >
                        {ArtistRowVirtual}
                      </List>
                    )}
                  </AutoSizer>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}

export default GenomeDetailPanel;
